import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getConsultorioProfile } from '../../api';
import { loadProfessionalProfile } from '../../utils/professionalProfile';
import { formatCodigoMspDisplay } from './ProfesionalSaludMsp';

/**
 * Muestra el profesional que atiende (perfil del consultorio / usuario), no datos del paciente.
 */
export default function ProfesionalAtencionCard({ compact = false }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [perfil, setPerfil] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const applyLocalFallback = () => {
      const local = loadProfessionalProfile(user?.email);
      if (!local) return null;
      const parts = String(local.nombreCompleto || '').trim().split(/\s+/);
      const nombre = parts[0] || '';
      const apellidos = parts.slice(1).join(' ');
      return {
        profesional_nombre: nombre,
        profesional_apellidos: apellidos,
        codigo_msp: local.registroMSP || '',
        firma_sello_data_url: '',
        firma_sello_presente: false,
        _soloLocal: true,
      };
    };

    getConsultorioProfile()
      .then(({ data }) => {
        if (cancelled) return;
        setPerfil(data);
      })
      .catch(() => {
        if (cancelled) return;
        setPerfil(applyLocalFallback());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.email, user?.tenant_id]);

  if (loading) {
    return (
      <div className="prof-atencion prof-atencion--loading">
        <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Espere" />
      </div>
    );
  }

  const nombre = [perfil?.profesional_nombre, perfil?.profesional_apellidos].filter(Boolean).join(' ').trim();
  const codigo = formatCodigoMspDisplay(perfil?.codigo_msp || '');
  const tieneFirma = !!(perfil?.firma_sello_presente || perfil?.firma_sello_data_url);
  const firmaUrl = perfil?.firma_sello_data_url || '';
  const incompleto = !nombre || !perfil?.codigo_msp;

  return (
    <div className={`prof-atencion${compact ? ' prof-atencion--compact' : ''}`}>
      <div className="prof-atencion__head">
        <i className="bi bi-person-badge-fill" aria-hidden />
        <div>
          <strong>Profesional que atiende</strong>
          <p className="prof-atencion__sub">
            Estos datos se configuran en <Link to="/consultorio/perfil-profesional">Mi perfil profesional</Link>, no en
            la ficha del paciente.
          </p>
        </div>
      </div>

      {incompleto ? (
        <div className="prof-atencion__alert prof-atencion__alert--warn">
          <i className="bi bi-exclamation-triangle-fill" aria-hidden />
          <div>
            <strong>Complete su perfil profesional</strong>
            <p>Registre nombre, apellidos y código MSP al dar de alta su usuario o en Mi perfil profesional.</p>
            <Link to="/consultorio/perfil-profesional" className="cp-btn cp-btn--primary cp-btn--sm mt-2">
              <i className="bi bi-pencil-square" />
              Ir a mi perfil
            </Link>
          </div>
        </div>
      ) : (
        <div className="prof-atencion__datos">
          <div className="prof-atencion__campo">
            <span className="prof-atencion__lbl">Nombre</span>
            <span className="prof-atencion__val">{nombre}</span>
          </div>
          <div className="prof-atencion__campo">
            <span className="prof-atencion__lbl">Código MSP</span>
            <span className="prof-atencion__val">{codigo || '—'}</span>
          </div>
        </div>
      )}

      {!tieneFirma ? (
        <div className="prof-atencion__alert prof-atencion__alert--firma">
          <i className="bi bi-cloud-upload" aria-hidden />
          <div>
            <strong>Sin firma y sello registrados</strong>
            <p>
              Para documentos MSP debe subir una <strong>imagen</strong> (foto o escaneo de su firma y sello) o un{' '}
              <strong>PDF</strong>, máximo 10 MB.
            </p>
            <ol className="prof-atencion__pasos">
              <li>Entre a Consultorio → Mi perfil profesional</li>
              <li>En «Firma y sello», elija el archivo desde su dispositivo</li>
              <li>Guarde el perfil</li>
            </ol>
            <Link to="/consultorio/perfil-profesional" className="cp-btn cp-btn--primary cp-btn--sm">
              <i className="bi bi-upload" />
              Subir firma y sello
            </Link>
          </div>
        </div>
      ) : (
        <div className="prof-atencion__firma-ok">
          <span className="prof-atencion__firma-badge">
            <i className="bi bi-check-circle-fill" />
            Firma y sello registrados
          </span>
          {firmaUrl.startsWith('data:image') ? (
            <img src={firmaUrl} alt="Firma y sello del profesional" className="prof-atencion__firma-img" />
          ) : (
            <span className="small text-muted">
              <i className="bi bi-file-earmark-pdf me-1" />
              Archivo PDF en perfil
            </span>
          )}
        </div>
      )}

      {perfil?._soloLocal ? (
        <p className="prof-atencion__nota-local small text-muted mb-0">
          <i className="bi bi-hdd" /> Datos cargados desde registro local; sincronice el perfil en el servidor cuando esté
          disponible.
        </p>
      ) : null}
    </div>
  );
}
